import { UseFormReturnType } from "@mantine/form";
import React, { useEffect, useMemo } from "react";
import { IForm } from "../type";
import {
  Box,
  Button,
  Card,
  Flex,
  Group,
  Input,
  List,
  rem,
  Select,
  Space,
  Text,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { IconChevronDown, IconInfoCircle } from "@tabler/icons-react";
import CurrencyInput from "@/components/common/currency-input/CurrencyInput";
import "../term-deposit.scss";
import { useLocation } from "react-router-dom";
import { TypeSavings } from "@/dto/savings/Savings.d";
import {
  useGetSavingsInterestByTypeQuery,
  useLazyCalSavingIRateQuery,
} from "@/services/savingsAPI";
import { useDebouncedValue } from "@mantine/hooks";
import FromAccount from "@/components/common/from-account/FromAccount";
import { GetMoneySource } from "@/dto/bank/response/BankInfo";
import i18n from "i18n/i18n";
import { beText } from "@/utils/beText";

const getDataMethodMaturity = () => ([
  { label: i18n.t("INTEREST_ROLLED_OVER_TO_PRINCIPAL"), value: "PRINCIPAL" },
  { label: i18n.t("RECYCLE_PRINCIPAL_AND_INTEREST_TO_SOURCE_ACCOUNT"), value: "INTEREST" },
  {
    label: i18n.t("NO_REVOLVING,_INTEREST_AND_PRINCIPAL_TRANSFERRED_TO_SOURCE_ACCOUNT"),
    value: "NONE",
  },
]);

const getDataMethodPeriodic = () => ([
  { label: i18n.t("PRINCIPAL_ROLLOVER"), value: "INTEREST" },
  {
    label: i18n.t("NO_ROLLOVER_PRINCIPAL_TO_SOURCE"),
    value: "NONE",
  },
]);
interface TermDepositStepOneProps {
  form: UseFormReturnType<IForm>;
  onSubmit: () => void;
  isLoading?: boolean;
  sources: GetMoneySource[];
  sourceLinkId: number;
  onChangeSourceLink: (id: number) => void;
}
const TermDepositStepOne: React.FC<TermDepositStepOneProps> = ({
  form,
  onSubmit,
  isLoading,
  sources,
  sourceLinkId,
  onChangeSourceLink,
}) => {
  const { t } = useTranslation();
  const formattedSources = useMemo(
    () =>
      sources.map((s) => ({
        ...s,
        sourceName: `TKTT-VND-${s.sourceName || s.displayName || ""}`,
      })),
    [sources],
  );
  const { state } = useLocation();
  const amountDebounce = useDebouncedValue(form.values.savingAmount, 1000);

  const { data: dataInterest } = useGetSavingsInterestByTypeQuery(
    {
      type: state?.type ?? "",
      frequency: form.values.interestPaymentCycle ?? "",
      method: form.values.savingsClassType ?? "",
      amount: (amountDebounce[0] ?? "").toString(),
    },
    {
      skip: !state?.type || !form.values.savingsClassType || !amountDebounce[0],
    },
  );

  const dataUnit =
    (dataInterest?.body.durations &&
      dataInterest?.body?.durations?.map((item: any) => ({
        // "3 Tháng - 3.85%/năm": don vi do BE ghep san, dich rieng phan chu.
        label: `${beText(item.displayDuration)} - ${beText(item.interestRateDisplay)}`,
        value: `${item.durationValue.toString()}-${item.durationUnit}`,
        durationunit: item.durationUnit,
        unitvalue: item.durationValue,
      }))) ||
    [];
  const [triggerCalRate, { data: dataRate }] = useLazyCalSavingIRateQuery();
  const isExpectedProfit = form.values.savingsClassType;

  useEffect(() => {
    if (
      state?.type &&
      amountDebounce[0] &&
      form.values.savingsDuration &&
      form.values.savingsClassType
    ) {
      triggerCalRate({
        amount: amountDebounce[0].toString(),
        type: state?.type,
        savingsMethod: form.values.savingsClassType,
        durationValue: Number(form.values.savingsDuration),
        durationUnit: form.values.durationUnit || "MONTH",
        savingsFrequency: form.values.interestPaymentCycle,
      });
    }
  }, [
    state?.type,
    amountDebounce[0],
    form.values.savingsDuration,
    form.values.savingsClassType,
    form.values.interestPaymentCycle,
    form.values.durationUnit,
  ]);

  useEffect(() => {
    if (dataRate && dataRate.body) {
      form.setValues({
        interest: dataRate.body.interestRate,
        calculatedInterestRate: dataRate.body.calculatedInterestRate,
      });
    }
  }, [dataRate]);

  useEffect(() => {
    form.setValues({
      sourceAccount: "",
      savingAmount: null,
      savingsType: "",
      savingsDuration: "",
      durationUnit: "",
      interestPaymentCycle: "",
      rollOverType: "",
      currency: "",
    });
    form.reset();
    if (state?.type !== TypeSavings.REGULAR_TERM_DEPOSIT) {
      form.setFieldValue("savingsClassType", "PAID_AT_MATURITY");
    }
  }, [state]);

  useEffect(() => {
    form.setFieldValue("savingsDurationType", "");
  }, [form.values.savingsClassType]);
  return (
    <Flex gap="lg" justify="space-between">
      <Card
        sx={{
          padding: "20px",
          height: "fit-content",
          display: "flex",
          flexDirection: "column",
          width: "65%",
        }}
      >
        <Box sx={{ flex: 1 }}>
          {/* Nội dung form */}
          <FromAccount
            sourceLinkId={sourceLinkId}
            sources={formattedSources}
            onChangeSourceLinkId={(id) => onChangeSourceLink(id)}
          />
          <Space h={24} />
          <Input.Wrapper label={t("DEPOSIT_AMOUNT")} mb="lg">
            <CurrencyInput
              placeholder={t("ENTER_DEPOSIT_AMOUNT") as string}
              {...form.getInputProps("savingAmount")}
              error={form.errors.savingAmount as string}
              thousandSeparator=","
            />
          </Input.Wrapper>

          {state?.type === TypeSavings.REGULAR_TERM_DEPOSIT ? (
            <Select
              data={[
                { label: t("START_OF_TERM"), value: "PAID_AT_BEGINNING" },
                { label: t("PERIODIC"), value: "PAID_PERIODICALLY" },
                { label: t("END_OF_TERM"), value: "PAID_AT_MATURITY" },
              ]}
              placeholder={t("INTEREST_COLLECTION_METHOD")}
              {...form.getInputProps("savingsClassType")}
              label={
                <Group spacing={6}>
                  <Text fw={500}>{t("INTEREST_COLLECTION_METHOD")}</Text>

                  <Tooltip
                    multiline
                    w={360}
                    position="right"
                    color="#fff"
                    withArrow
                    label={
                      <>
                        <Text fz={14} c="#000" fw={600}>
                          {t("INTEREST_COLLECTION_METHOD")}
                        </Text>
                        <List>
                          <List.Item>
                            <Text fz={13} c="#99A0AE" fw={400}>
                              {t("START_OF_TERM_INTEREST_NOTE")}
                            </Text>
                          </List.Item>
                          <List.Item>
                            <Text fz={13} mt={4} c="#99A0AE" fw={400}>
                              {t("PERIODIC_INTEREST_NOTE")}
                            </Text>
                          </List.Item>
                          <List.Item>
                            <Text fz={13} mt={4} c="#99A0AE" fw={400}>
                              {t("END_OF_TERM_INTEREST_NOTE")}
                            </Text>
                          </List.Item>
                        </List>
                      </>
                    }
                  >
                    <IconInfoCircle size={16} style={{ cursor: "pointer" }} />
                  </Tooltip>
                </Group>
              }
              rightSection={<IconChevronDown size="1rem" />}
              mb="lg"
            />
          ) : (
            <>
              <Select
                readOnly
                data={[
                  { label: t("START_OF_TERM"), value: "PAID_AT_BEGINNING" },
                  { label: t("PERIODIC"), value: "PAID_PERIODICALLY" },
                  { label: t("END_OF_TERM"), value: "PAID_AT_MATURITY" },
                ]}
                label={t("INTEREST_COLLECTION_METHOD")}
                mb="lg"
                styles={{ input: { background: "#f5f5f5" } }}
                {...form.getInputProps("savingsClassType")}
              />
              <Select
                label={
                  state?.type === TypeSavings.RECURRING_DEPOSIT
                    ? t("ORIGINAL_TERM")
                    : t("TERM_TYPE")
                }
                placeholder={t("SELECT_TERM_TYPE") as string}
                data={dataUnit}
                {...form.getInputProps("savingsDurationType")}
                onChange={(e) => {
                  const findValue = dataUnit.find((item) => item.value === e);
                  form.setValues({
                    savingsDurationType: findValue?.value || "",
                    savingsDuration: findValue?.unitvalue || "",
                    durationUnit: findValue?.durationunit || "",
                  });
                }}
                rightSection={<IconChevronDown size="1rem" />}
                error={form.errors.savingsDurationType}
                mb="lg"
              />
              {form.values.interest && (
                <Input.Wrapper label={t("INTEREST_RATE")} mb="lg">
                  <CurrencyInput
                    disabled
                    {...form.getInputProps("interest")}
                    rightSection={`%/${t("YEAR_LOWERCASE")}`}
                    thousandSeparator=","
                  />
                </Input.Wrapper>
              )}
            </>
          )}
          {isExpectedProfit === "PAID_PERIODICALLY" && (
            <Select
              data={[
                { label: t("MONTHLY"), value: "MONTHLY" },
                { label: t("QUARTERLY"), value: "QUARTERLY" },
              ]}
              label={t("INTEREST_PAYMENT_CYCLE")}
              placeholder={t("SELECT_INTEREST_PAYMENT_CYCLE")}
              mb="lg"
              rightSection={<IconChevronDown size="1rem" />}
              {...form.getInputProps("interestPaymentCycle")}
            />
          )}
          {state?.type === TypeSavings.REGULAR_TERM_DEPOSIT && (
            <Select
              label={
                <Group spacing={6}>
                  <Text fw={500}>{t("TERM_TYPE")}</Text>

                  <Tooltip
                    multiline
                    w={360}
                    color="#fff"
                    position="right"
                    withArrow
                    label={
                      <>
                        <Text fz={14} c="#000" fw={600}>
                          {t("TERM_TYPE")}
                        </Text>
                        <Text fz={13} c="#99A0AE">
                          {t("TERM_9_MONTHS_4_5")}
                        </Text>
                        <Text fz={13} mt={4} c="#99A0AE">
                          {t("TERM_12_MONTHS_5_2")}
                        </Text>
                        <Text fz={13} mt={4} c="#99A0AE">
                          {t("TERM_18_MONTHS_5_5")}
                        </Text>
                        <Text fz={13} c="#99A0AE" mt={4}>
                          {t("TERM_24_MONTHS_6_2")}
                        </Text>
                      </>
                    }
                  >
                    <IconInfoCircle size={16} style={{ cursor: "pointer" }} />
                  </Tooltip>
                </Group>
              }
              placeholder={t("SELECT_TERM_TYPE") as string}
              data={dataUnit}
              {...form.getInputProps("savingsDurationType")}
              onChange={(e) => {
                const findValue = dataUnit.find((item) => item.value === e);
                form.setValues({
                  savingsDurationType: findValue?.value || "",
                  savingsDuration: findValue?.unitvalue || "",
                  durationUnit: findValue?.durationunit || "",
                });
              }}
              rightSection={<IconChevronDown size="1rem" />}
              error={form.errors.savingsDurationType}
              mb="lg"
            />
          )}

          {/* {form.values.termType && form.values.depositAmount && (
            <Input.Wrapper label="Lãi suất" mb="lg">
              <CurrencyInput
                disabled
                {...form.getInputProps("interest")}
                rightSection="%/năm"
              />
            </Input.Wrapper>
          )} */}

          {state?.type === TypeSavings.REGULAR_TERM_DEPOSIT &&
            form.values.interest &&
            isExpectedProfit && (
              <>
                <Input.Wrapper label={t("INTEREST_RATE")} mb="lg">
                  <CurrencyInput
                    disabled
                    {...form.getInputProps("interest")}
                    rightSection={`%/${t("YEAR_LOWERCASE")}`}
                    thousandSeparator=","
                  />
                </Input.Wrapper>
                <Input.Wrapper
                  label={(() => {
                    if (isExpectedProfit === "PAID_AT_MATURITY") {
                      return t("EXPECTED_PROFIT_AT_THE_END_OF_THE_PERIOD");
                    }
                    if (isExpectedProfit === "PAID_AT_BEGINNING") {
                      return t("EXPECTED_START_OF_TERM_INTEREST");
                    }
                    return t("PERIODIC_INTEREST_PAYMENT");
                  })()}
                  mb="lg"
                >
                  <CurrencyInput
                    placeholder=""
                    {...form.getInputProps("calculatedInterestRate")}
                    disabled
                    thousandSeparator=","
                  />
                </Input.Wrapper>
              </>
            )}

          <Select
            label={
              <Group spacing={6}>
                <Text fw={500}>
                  {t("FORMS_OF_PRINCIPAL_AND_INTEREST_RENEWAL")}
                </Text>

                <Tooltip
                  multiline
                  w={360}
                  color="#fff"
                  position="right"
                  withArrow
                  label={
                    <>
                      <Text fz={14} c="#000" fw={600}>
                        {t("FORMS_OF_PRINCIPAL_AND_INTEREST_RENEWAL")}
                      </Text>
                      <Text fz={13} c="#99A0AE">
                        {t("COMPOUND_ROLLOVER_NOTE")}
                      </Text>
                      <Text fz={13} mt={4} c="#99A0AE">
                        {t("PRINCIPAL_ROLLOVER_NOTE")}
                      </Text>
                      <Text fz={13} mt={4} c="#99A0AE">
                        {t("NO_ROLLOVER_NOTE")}
                      </Text>
                    </>
                  }
                >
                  <IconInfoCircle size={16} style={{ cursor: "pointer" }} />
                </Tooltip>
              </Group>
            }
            placeholder={t("FORMS_OF_PRINCIPAL_AND_INTEREST_RENEWAL") as string}
            data={
              // eslint-disable-next-line no-nested-ternary
              isExpectedProfit === "PAID_AT_MATURITY"
                ? getDataMethodMaturity()
                : isExpectedProfit === "PAID_PERIODICALLY" ||
                  isExpectedProfit === "PAID_AT_BEGINNING"
                ? getDataMethodPeriodic()
                : []
            }
            {...form.getInputProps("rollOverType")}
            rightSection={<IconChevronDown size="1rem" />}
            error={form.errors.rollOverType}
            mb="lg"
          />
        </Box>

        {/* Footer luôn ở cuối */}
        <Group position="center" mt={rem(20)}>
          <Box
            style={{
              position: "fixed",
              bottom: 0,
              right: 20,
              width: "calc(100% - var(--mantine-navbar-width, 0px) - 40px)",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "right",
              gap: "10px",
              boxShadow: "0px 5px 15px 0px #00000026",
              height: "76px",
              alignItems: "center",
              backgroundColor: "white",
              zIndex: 1,
            }}
          >
            <Button
              sx={() => ({
                width: "160px",
                color: "white",
                borderRadius: "8px",
                padding: "8px 10px",
                cursor: "pointer",
                fontWeight: 400,
                ":hover": {
                  color: "white",
                },
                background: "#2A81D0",
              })}
              loading={isLoading}
              type="submit"
              onClick={onSubmit}
            >
              {t("NEXT")}
            </Button>
          </Box>
        </Group>
      </Card>
      {state?.type === TypeSavings.RECURRING_DEPOSIT && (
        <Card p="lg" radius="16px" w="35%" h="fit-content">
          <Text fw={600} fz={20} c="#212936">
            {t("RECURRING_DEPOSIT_INFORMATION")}
          </Text>
          <Space h={20} />
          <Select
            w="100%"
            data={[
              { label: t("MANUAL"), value: "MANUAL" },
              { label: t("AUTOMATIC"), value: "AUTO" },
            ]}
            label={t("DEPOSIT_METHOD")}
            {...form.getInputProps("method")}
          />
          {String(form.values.method) === "AUTO" && (
            <>
              <Select
                w="100%"
                mt="lg"
                data={[
                  { label: t("MONTHLY"), value: "MONTHLY" },
                  { label: t("QUARTERLY"), value: "QUARTERLY" },
                ]}
                label={t("DEPOSIT_CYCLE")}
                {...form.getInputProps("frequency")}
              />
              <Input.Wrapper label={t("RECURRING_DEPOSIT")} my="lg">
                <CurrencyInput
                  {...form.getInputProps("amount")}
                  thousandSeparator=","
                />
              </Input.Wrapper>
            </>
          )}
        </Card>
      )}
    </Flex>
  );
};

export default TermDepositStepOne;
